/**
 * **Website → Booking flows** — the list (C19, FR-J11 item 1, ADR-0037 §6;
 * Wave 19B, T-5.59; reshaped by C20, Wave 20C, T-5.68). A `ListPage` over the
 * kit's `DataTable`: each flow's name, status, address and how many services
 * it offers. A row opens the flow's edit page (`FlowEditor`); *New flow*
 * opens the same page empty.
 *
 * The address is `/book/<slug>` **on the firm's own website** (ADR-0040 §3),
 * never on the platform host this screen is on.
 *
 * Reading needs `booking:can_view`; *New flow* needs `booking:can_update`
 * (T-5.57: every write to a flow is `can_update`, create included). The
 * service enforces both; this screen only declines to offer what it would
 * refuse.
 */

import { Button } from '@fiveox/ui'
import { Plus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import {
  type ListColumn,
  ListPage,
  ListTable,
  ListToolbar,
  StatusBadge,
  statusTone,
  useListTable,
} from '../../../components/page'
import { BOOKING, useEvaluation } from '../../../lib/access'
import { ApiError } from '../../../lib/api'
import { type BookingFlow, booking } from '../../../lib/api/booking'

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** What a flow offers, in a word: a count, or *All on your site* when it
 *  names none (`offer.services: []` is the site-visible catalog). */
export function servicesCount(flow: BookingFlow): string {
  const n = flow.offer.services.length
  return n === 0 ? 'All on your site' : String(n)
}

export function BookingFlows() {
  const { can } = useEvaluation()
  const navigate = useNavigate()
  const [flows, setFlows] = useState<BookingFlow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    booking
      .flows()
      .then((body) => {
        if (live) setFlows(body.data)
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof ApiError ? err.message : 'Your booking flows could not be loaded.')
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [])

  const columns = useMemo<ListColumn<BookingFlow>[]>(
    () => [
      {
        key: 'name',
        label: 'Flow',
        width: 200,
        required: true,
        phone: true,
        sortField: 'name',
        render: (row) => (
          <span className="block min-w-0">
            <span className="block truncate font-medium">{row.name}</span>
            <span className="text-muted-foreground block truncate font-mono text-xs">{row.path}</span>
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        width: 100,
        phone: true,
        sortField: 'status',
        render: (row) => <StatusBadge kind="flow" status={row.status} />,
      },
      {
        key: 'services',
        label: 'Services',
        width: 110,
        align: 'right',
        render: (row) => <span data-services-count="">{servicesCount(row)}</span>,
      },
      {
        key: 'bookings',
        label: 'Bookings',
        width: 100,
        align: 'right',
        render: (row) => <span className="tabular-nums">{row.bookings ?? '—'}</span>,
      },
    ],
    [],
  )

  const list = useListTable(flows, {
    columns,
    search: (row) => [row.name, row.slug],
    filters: [
      {
        key: 'status',
        label: 'Status',
        allLabel: 'Any status',
        options: (['draft', 'live', 'paused'] as const).map((value) => ({
          value,
          label: statusTone('flow', value).label,
        })),
        match: (row, value) => row.status === value,
      },
    ],
    initialSort: { field: 'name' },
  })

  const newFlow = can(BOOKING, 'can_update') ? (
    <Button onClick={() => navigate('/site/booking/new')}>
      <Plus size={16} aria-hidden /> New flow
    </Button>
  ) : undefined

  const live = flows.filter((f) => f.status === 'live').length

  return (
    <ListPage
      back={{ to: '/site', label: 'Your website' }}
      title="Booking flows"
      description={`Visitors book on your website: their details, a code to their email, a plan, and payment. Link any website button to a live one.${
        loading ? '' : ` ${plural(flows.length, 'flow')}, ${live} live.`
      }`}
      primary={newFlow}
      loading={loading}
      error={error}
      isEmpty={list.isEmpty}
      empty={{
        title: 'No booking flows yet',
        description:
          'A flow is one short form: what you ask, what they can book, payment and the wording. Save it as a draft, then set it live.',
        action: newFlow,
      }}
      toolbar={<ListToolbar list={list} placeholder="Search by name or address" />}
    >
      <ListTable list={list} rowKey={(row) => row.id} rowHref={(row) => `/site/booking/${row.id}`} />
    </ListPage>
  )
}
